import React, { useState, useContext, useCallback, useEffect, useRef } from 'react';
import _ from 'lodash';

import { ViewModelForm, ViewModelServiceContext } from '@xengage/gw-portals-viewmodel-react';
import { useValidation } from '@xengage/gw-portals-validation-react';

import { EMAIL_VALIDATION_STATUS, WMICDateTimeService, JobType } from 'wmic-pe-portals-utils-js';

import { AccountService } from 'wmic-pe-capability-gateway-policy';
import metadata from './WMICDriveChangeTelematicsComponent.metadata.json5';

import messages from './WMICDriveChangeTelematicsComponent.messages.js';
import styles from './WMICDriveChangeTelematicsComponent.module.scss';
import { TranslatorContext } from '@jutro/legacy/locale';

function WMICDriveChangeTelematicsComponent(props) {
    const {
        id,
        jobVM,
        driverVM,
        showErrors,
        updateDriver,
        authHeader,
        onValidate,
        isEditMode,
        isReadOnlyUser,
        hideButton = false,
    } = props;

    const { onValidate: onValidateComponent, isComponentValid, registerComponentValidation } = useValidation(id);
    const translator = useContext(TranslatorContext);
    const viewModelService = useContext(ViewModelServiceContext);

    const [emailValidationStatus, setEmailValidationStatus] = useState('');
    const [emailAddressHasChanged, setEmailAddressHasChanged] = useState(false);
    const [showEmailValidationMessage, setShowEmailValidationMessage] = useState(false);
    const [isEmailVerifying, setIsEmailVerifying] = useState(false);
    const [driverEmailAddress, setDriverEmailAddress] = useState(_.get(driverVM, 'emailAddress1.value', ''));
    const [showConfirmationEmail, setShowConfirmationEmail] = useState();
    const [doesEmailNeedReVerification, setDoesEmailNeedReVerification] = useState(false);
    const [isOriginalEmailEnrolledInDriveChange, setIsOriginalEmailEnrolledInDriveChange] = useState(
        driverVM.driveChange_WMIC.isEmailEnrolled.value
    );
    const originalEmail = useRef(driverVM.emailAddress1.value).current;

    const driveChangeEnrollmentStatusTypelist = viewModelService.productMetadata
        .get('pc')
        .types.getTypelist('DriveChangeEnrollmentStatus_Ext');
    const driveChangeUnenrollmentReasonTypelist = viewModelService.productMetadata
        .get('pc')
        .types.getTypelist('DriveChangeUnenrollRsn_Ext');

    useEffect(() => {
        if (onValidate) {
            onValidate(isComponentValid, id);
        }
    }, [isComponentValid, onValidate, id]);

    const isNewEmailEnrolledInDriveChange = useCallback(
        () =>
            !!driverVM.emailAddress1.value &&
            driverVM.emailAddress1.value !== originalEmail &&
            driverVM.driveChange_WMIC.isEmailEnrolled.value === true,
        [driverVM, originalEmail]
    );

    const emailEnrolledInDriveChangeValidation = useCallback(
        () =>
            !(
                doesEmailNeedReVerification ||
                (driverVM.driveChange_WMIC.isEnrolled.value && isNewEmailEnrolledInDriveChange())
            ),
        [doesEmailNeedReVerification, driverVM, isNewEmailEnrolledInDriveChange]
    );

    useEffect(() => {
        registerComponentValidation(emailEnrolledInDriveChangeValidation);
    }, [registerComponentValidation, emailEnrolledInDriveChangeValidation]);

    const getValidationStatus = useCallback(() => {
        switch (emailValidationStatus) {
            case EMAIL_VALIDATION_STATUS.VALID:
                return messages.emailValid;
            case EMAIL_VALIDATION_STATUS.INVALID_TLS:
                return messages.invalidtls;
            case EMAIL_VALIDATION_STATUS.ERROR:
                return messages.unavailable;
            case EMAIL_VALIDATION_STATUS.INVALID:
                return messages.invalid;
            case EMAIL_VALIDATION_STATUS.EMPTY:
                return messages.emptyEmail;
            default:
                return messages.unavailable;
        }
    }, [emailValidationStatus]);

    const onEmailAddressChange = (value, path) => {
        _.set(driverVM, path, value);
        if (driverEmailAddress === driverVM.emailAddress1.value) {
            setShowEmailValidationMessage(
                emailValidationStatus !== EMAIL_VALIDATION_STATUS.DRAFT && emailValidationStatus !== ''
            );
            setEmailAddressHasChanged(false);
            _.set(driverVM, 'confirmDriverEmailAddress_WMIC.value', driverEmailAddress);
            driverVM.driveChange_WMIC.isEmailEnrolled.value = isOriginalEmailEnrolledInDriveChange;
        } else {
            setEmailValidationStatus('');
            setShowEmailValidationMessage(false);
            setEmailAddressHasChanged(true);
            _.set(driverVM, 'confirmDriverEmailAddress_WMIC.value', null);
            setDoesEmailNeedReVerification(true);
            driverVM.driveChange_WMIC.isEmailEnrolled.value = false;
        }
        updateDriver(driverVM);
    };

    const validateEmail = () => {
        if (
            showConfirmationEmail &&
            (!_.get(driverVM, 'confirmDriverEmailAddress_WMIC.aspects.valid') ||
                !_.get(driverVM, 'confirmDriverEmailAddress_WMIC.value'))
        ) {
            return;
        }
        setEmailAddressHasChanged(false);
        setIsEmailVerifying(true);
        AccountService.validateDriveChangeEmail(driverVM?.emailAddress1?.value, authHeader)
            .then(res => {
                setIsEmailVerifying(false);
                setEmailValidationStatus(res.emailValidationStatus);
                setShowEmailValidationMessage(
                    res.emailValidationStatus !== EMAIL_VALIDATION_STATUS.DRAFT && res.emailValidationStatus !== ''
                );
                setDoesEmailNeedReVerification(false);
                driverVM.driveChange_WMIC.isEmailEnrolled.value = res.isEmailEnrolledInDriveChange;
            })
            .catch(() => {
                setIsEmailVerifying(false);
                setEmailValidationStatus(EMAIL_VALIDATION_STATUS.ERROR);
                setShowEmailValidationMessage(true);
                setDoesEmailNeedReVerification(true);
            });
    };

    const displayDate = dateValue => (dateValue ? <span>{WMICDateTimeService.toMidnightDate(dateValue)}</span> : '-');

    const displayDates = dateValues => (
        <ul>{dateValues ? dateValues.map(dateValue => <li>{displayDate(dateValue)}</li>) : '-'}</ul>
    );

    const isDriverEnrolled = driverVM.driveChange_WMIC?.isEnrolled?.value;
    const isRPCEffectiveForDCV2 = driverVM.driveChange_WMIC?.isRPCEffectiveForDCV2?.value;
    const areWeeklyScoresAndAdjustmentDatesVisible = isDriverEnrolled && isRPCEffectiveForDCV2;
    const isCurrentDetailsDataListVisible = isDriverEnrolled && isRPCEffectiveForDCV2;
    const areCurrentDetailsVisibleInDrivingDetails = isDriverEnrolled && !isRPCEffectiveForDCV2;
    const isBillingInfoAvailable =
        jobVM?.baseData?.jobType?.value?.code === JobType.POLICY_CHANGE ||
        jobVM?.baseData?.jobType?.value?.code === JobType.RENEWAL;
    const isPaymentPlanNotMAR = !jobVM?.baseData?.isMonthlyPaymentPlan_Ext?.value;
    const isMARRequiredWarningVisible =
        isDriverEnrolled && isRPCEffectiveForDCV2 && isBillingInfoAvailable && isPaymentPlanNotMAR;

    const overrideProps = {
        '@field': {
            parentNode: driverVM,
            readOnly: !isEditMode || isReadOnlyUser,
        },
        isDriveEnrolled: {
            readOnly: !driverVM?.driveChange_WMIC?.isEnrolled?.aspects?.editable || !isEditMode || isReadOnlyUser,
        },
        monthlyAutomatedPaymentRequiredWarning: {
            visible: isMARRequiredWarningVisible,
        },
        hasAlreadyEnrolledInAnotherPolicy: {
            visible: driverVM?.driveChange_WMIC?.isDriverAlreadyEnrolledInDriveChange?.value === true,
        },
        emailValidationIcon: {
            visible: emailValidationStatus === EMAIL_VALIDATION_STATUS.VALID,
        },
        emailValidationStatus: {
            visible: !_.isNil(getValidationStatus()),
            content: translator(getValidationStatus()),
        },
        isEmailEnrolledInDriveChange: {
            visible: isNewEmailEnrolledInDriveChange(),
        },
        pleaseVerifyEmail: {
            visible: doesEmailNeedReVerification === true,
        },
        driveChangeContactInfoWarning: {
            visible: !(_.get(driverVM, 'emailAddress1.value', false) && _.get(driverVM, 'cellPhone_WMIC.value', false)),
        },
        enrollmentDetailsDataList: {
            VMList: [driverVM],
            dataListTitle: { getData: () => messages.enrollmentDetails },
            VMData: [
                {
                    headerText: translator(messages.activationDate),
                    getData: () => displayDate(_.get(driverVM, 'driveChange_WMIC.activationDate.value')),
                    visibilityCondition: () => _.get(driverVM, 'driveChange_WMIC.isEnrolled.value', false),
                },
                {
                    headerText: translator(messages.enrollmentStatus),
                    getData: () =>
                        _.get(driverVM, 'driveChange_WMIC.enrollmentStatus.value.code')
                            ? translator({
                                  id: driveChangeEnrollmentStatusTypelist.getCode(
                                      _.get(driverVM, 'driveChange_WMIC.enrollmentStatus.value.code')
                                  ).name,
                              })
                            : '-',
                    visibilityCondition: () => _.get(driverVM, 'driveChange_WMIC.isEnrolled.value', false),
                },
                {
                    headerText: translator(messages.lastUpdatedOn),
                    getData: () =>
                        displayDate(_.get(driverVM, 'driveChange_WMIC.lastEnrollmentStatusUpdateDate.value')),
                    visibilityCondition: () => _.get(driverVM, 'driveChange_WMIC.isEnrolled.value', false),
                },
                {
                    headerText: translator(messages.unenrollmentReason),
                    getData: () =>
                        _.get(driverVM, 'driveChange_WMIC.unenrollmentReason.value.code')
                            ? translator({
                                  id: driveChangeUnenrollmentReasonTypelist.getCode(
                                      _.get(driverVM, 'driveChange_WMIC.unenrollmentReason.value.code')
                                  ).name,
                              })
                            : '-',
                    visibilityCondition: () => driverVM.driveChange_WMIC?.unenrollmentReason?.aspects?.ocular,
                },
                {
                    headerText: translator(messages.unenrollmentDate),
                    getData: () => displayDate(_.get(driverVM, 'driveChange_WMIC.unenrollmentDate.value')),
                    visibilityCondition: () => driverVM.driveChange_WMIC?.unenrollmentDate?.aspects?.ocular,
                },
            ],
            readOnly: true,
            clickable: false,
        },
        drivingDetailsDataList: {
            VMList: [driverVM],
            dataListTitle: {
                getData: () => (isRPCEffectiveForDCV2 ? messages.ratedDriverDetails : messages.drivingDetails),
            },
            VMData: [
                {
                    headerText: translator(messages.ratedDriverScore),
                    getData: () => driverVM.driveChange_WMIC?.safetyScore?.value ?? '-',
                    visibilityCondition: () => driverVM.driveChange_WMIC?.safetyScore?.aspects?.ocular,
                },
                {
                    headerText: translator(messages.currentDriverScore),
                    getData: () => driverVM.driveChange_WMIC?.currentDriverScore?.value ?? '-',
                    visibilityCondition: () => areCurrentDetailsVisibleInDrivingDetails,
                },
                {
                    headerText: translator(messages.ratedKilometersDriven),
                    getData: () => driverVM.driveChange_WMIC?.termDistanceDriven?.value ?? '-',
                    visibilityCondition: () => driverVM.driveChange_WMIC?.termDistanceDriven?.aspects?.ocular,
                },
                {
                    headerText: translator(messages.currentKilometersDriven),
                    getData: () => driverVM.driveChange_WMIC?.currentKilometersDriven?.value ?? '-',
                    visibilityCondition: () => areCurrentDetailsVisibleInDrivingDetails,
                },
                {
                    headerText: translator(messages.ratedTripsTaken),
                    getData: () => driverVM.driveChange_WMIC?.ratedTripsTaken?.value ?? '-',
                    visibilityCondition: () => driverVM.driveChange_WMIC?.ratedTripsTaken?.aspects?.ocular,
                },
                {
                    headerText: translator(messages.currentTripsTaken),
                    getData: () => driverVM.driveChange_WMIC?.currentTripsTaken?.value ?? '-',
                    visibilityCondition: () => areCurrentDetailsVisibleInDrivingDetails,
                },
                {
                    headerText: translator(messages.lastTripDate),
                    getData: () => displayDate(driverVM.driveChange_WMIC?.lastTripDate?.value),
                    visibilityCondition: () => areCurrentDetailsVisibleInDrivingDetails,
                },
                {
                    headerText: translator(messages.lastSessionDate),
                    getData: () => displayDate(driverVM.driveChange_WMIC?.lastSessionDate?.value),
                    visibilityCondition: () => driverVM.driveChange_WMIC?.lastSessionDate?.aspects?.ocular,
                },
            ],
            readOnly: true,
            clickable: false,
            visible: isDriverEnrolled,
        },
        currentDriverDetailsDataList: {
            VMList: [driverVM],
            dataListTitle: { getData: () => messages.currentDriverDetails },
            VMData: [
                {
                    headerText: translator(messages.currentDriverScore),
                    getData: () => driverVM.driveChange_WMIC?.currentDriverScore?.value ?? '-',
                },
                {
                    headerText: translator(messages.currentKilometersDriven),
                    getData: () => driverVM.driveChange_WMIC?.currentKilometersDriven?.value ?? '-',
                },
                {
                    headerText: translator(messages.currentTripsTaken),
                    getData: () => driverVM.driveChange_WMIC?.currentTripsTaken?.value ?? '-',
                },
                {
                    headerText: translator(messages.lastTripDate),
                    getData: () => displayDate(driverVM.driveChange_WMIC?.lastTripDate?.value),
                },
            ],
            readOnly: true,
            clickable: false,
            visible: isCurrentDetailsDataListVisible,
        },
        weeklyScoresDataList: {
            VMList: [driverVM],
            dataListTitle: { getData: () => messages.weeklyScores },
            VMData: [
                {
                    headerText: translator(messages.sevenDayRollingScore),
                    getData: () => driverVM.driveChange_WMIC?.weeklyRollingScore?.value ?? '-',
                },
                {
                    headerText: translator(messages.previousWeekRollingScore),
                    getData: () => driverVM.driveChange_WMIC?.previousWeekRollingScore?.value ?? '-',
                },
            ],
            readOnly: true,
            clickable: false,
            visible: areWeeklyScoresAndAdjustmentDatesVisible,
        },
        adjustmentDates: {
            VMList: [driverVM],
            VMData: [
                {
                    headerText: translator(messages.adjustmentDates),
                    getData: () => displayDates(driverVM.driveChange_WMIC?.adjustmentDates?.value),
                },
            ],
            readOnly: true,
            clickable: false,
            visible: areWeeklyScoresAndAdjustmentDatesVisible,
        },
        driveChangeDetailsContainer: {
            visible: _.get(driverVM, 'driveChange_WMIC.driveChangeDetailsContainer.aspects.ocular', false),
        },
        driveChangeFieldset: {
            visible: _.get(driverVM, 'driveChange_WMIC.isEnrolled.value', false),
        },
        driverConfirmEmail: {
            visible: emailAddressHasChanged,
        },
        acctEmailValidation: {
            visible: showEmailValidationMessage,
        },
        previousDiscountPercentage: {
            readOnly:
                !_.get(driverVM, 'driveChange_WMIC.previousDiscountPercentage.aspects.editable') ||
                !isEditMode ||
                isReadOnlyUser,
        },
        verifyEmailButton: {
            disabled: isEmailVerifying || !isEditMode || isReadOnlyUser,
            visible: !hideButton && emailValidationStatus !== EMAIL_VALIDATION_STATUS.VALID,
        },
    };

    const resolvers = {
        resolveCallbackMap: {
            onEmailAddressChange,
            validateEmail,
        },
        resolveClassNameMap: styles,
    };

    return (
        <ViewModelForm
            uiProps={metadata.componentContent}
            model={driverVM}
            onModelChange={updateDriver}
            overrideProps={overrideProps}
            onValidationChange={onValidateComponent}
            classNameMap={resolvers.resolveClassNameMap}
            callbackMap={resolvers.resolveCallbackMap}
            showErrors={showErrors}
        />
    );
}

export default WMICDriveChangeTelematicsComponent;
